import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { MdAdd, MdBusiness, MdPersonAddAlt1 } from "react-icons/md";
import { CalcOption } from "../../../../types/budget/budget";
import SupplierAutoComplete from "../../SupplierAutoComplete";
import { isTemporarySupplierOption } from "../../../../utils/budget-supplier-id";
import { TransferTarget } from "../TransferCategorySupplierModal";
import type { TransferValueProjection } from "../../../../utils/budget-service-transfer-projection";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import {
  CALC_OPTION_SHORT_LABEL,
  COLORS,
  ChoiceCard,
  ConsequenceList,
  Highlight,
  InfoBox,
  ModalCloseBar,
  ModalHero,
  SectionLabel,
  SuccessStep,
  SummaryBlock,
  SummaryRow,
  WarningBox,
  WarningHero,
  cancelButtonSx,
  modalActionsSx,
  primaryButtonSx,
} from "../CategoryMoveModals/parts";

type Step = "mode" | "pick" | "confirm" | "done";
type Mode = "existing" | "new";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Nome do serviço exibido na linha (ex.: "Diária de hospedagem"). */
  serviceLabel: string;
  categoryLabel: string;
  currentSupplierLabel: string;
  /** `_id` real do fornecedor de origem — bloqueia a seleção dele na busca. */
  currentSupplierRealId: string;
  calcOption: CalcOption;
  /** Fornecedores já cadastrados NESTA forma de cobrança, exceto o atual. */
  existingSuppliers: Array<{
    supplierId: string;
    realId: string;
    label: string;
  }>;
  /**
   * A categoria do serviço já existe no fornecedor de destino? Decide qual dos
   * dois textos de confirmação é exibido — e, principalmente, se o valor do
   * serviço pode mudar.
   */
  categoryExistsIn: (targetSupplierId: string) => boolean;
  /**
   * Valor atual × projetado do serviço no destino, calculado antes do dispatch.
   * `null` quando não há o que projetar. Opcional para o modal continuar
   * renderizável sem contexto de orçamento (testes).
   */
  projectValue?: (target: TransferTarget) => TransferValueProjection | null;
  onConfirm: (target: TransferTarget) => Promise<boolean>;
};

/** Consequências comuns a todos os cenários. */
const CONSEQUENCES: React.ReactNode[] = [
  "Tarifa, quantidade, diárias, datas, descritivos e observações do serviço são mantidos.",
  "O fornecedor e a categoria de origem permanecem no orçamento, mesmo que fiquem sem serviços.",
  "Taxas lançadas em valor fixo na categoria de origem são redistribuídas entre os serviços que ficaram.",
  "Os totais da origem, do destino e do orçamento serão recalculados.",
  "A ação será registrada na auditoria.",
];

/**
 * Valor atual × projetado do serviço no destino.
 *
 * Quando os dois batem, a mensagem é de tranquilidade — é o caso da categoria
 * criada automaticamente, que herda o perfil fiscal da origem. Quando divergem,
 * o número é o dado que faltava: sem ele o usuário só descobre o impacto depois
 * de confirmar.
 */
const ValueProjection: React.FC<{ projection: TransferValueProjection }> = ({
  projection,
}) => {
  const { current, projected, delta, changes } = projection;
  const deltaColor = delta > 0 ? COLORS.warnIcon : COLORS.success;

  return (
    <Box
      data-testid="value-projection"
      sx={{
        backgroundColor: changes ? COLORS.warnBg : COLORS.surface,
        border: `1px solid ${
          changes ? COLORS.warnBorder : COLORS.surfaceBorder
        }`,
        borderRadius: "8px",
        px: 2,
        py: 1.4,
        mt: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 13, color: COLORS.muted, minWidth: 128 }}>
          Valor atual:
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
          {currencyFormatter.format(current)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: COLORS.muted, minWidth: 128 }}>
          Valor no destino:
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: changes ? deltaColor : COLORS.text,
          }}
        >
          {currencyFormatter.format(projected)}
          {changes && (
            <Box component="span" sx={{ ml: 0.75, fontWeight: 600 }}>
              ({delta > 0 ? "+" : "−"}
              {currencyFormatter.format(Math.abs(delta))})
            </Box>
          )}
        </Typography>
      </Box>
      <Typography
        sx={{ fontSize: 12.5, color: COLORS.muted, mt: 1, lineHeight: 1.5 }}
      >
        {changes
          ? "A diferença vem das taxas, impostos e câmbio da categoria de destino, que passam a valer para este serviço."
          : "O valor do serviço não muda com a transferência."}
      </Typography>
    </Box>
  );
};

const TransferServiceSupplierModal: React.FC<Props> = ({
  open,
  onClose,
  serviceLabel,
  categoryLabel,
  currentSupplierLabel,
  currentSupplierRealId,
  calcOption,
  existingSuppliers,
  categoryExistsIn,
  projectValue,
  onConfirm,
}: Props) => {
  const [step, setStep] = useState<Step>("mode");
  const [mode, setMode] = useState<Mode | "">("");
  const [existingId, setExistingId] = useState("");
  const [newOption, setNewOption] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const hasExistingSuppliers = existingSuppliers.length > 0;
  const calcLabel = CALC_OPTION_SHORT_LABEL[calcOption];

  useEffect(() => {
    if (open) {
      setStep("mode");
      // Sem outro fornecedor na forma de cobrança, "Fornecedor existente" fica
      // indisponível — já abre com "Novo fornecedor" pré-selecionado.
      setMode(hasExistingSuppliers ? "" : "new");
      setExistingId("");
      setNewOption(null);
      setSaving(false);
    }
  }, [open, hasExistingSuppliers]);

  const target: TransferTarget | null = useMemo(() => {
    if (mode === "existing") {
      const found = existingSuppliers.find((s) => s.supplierId === existingId);
      if (!found) return null;
      return {
        realSupplierId: found.realId,
        existingSupplierId: found.supplierId,
        supplierName: found.label,
        isNew: false,
      };
    }
    if (mode === "new" && newOption?._id) {
      return {
        realSupplierId: newOption._id,
        supplierName: newOption.name || "",
        isNew: true,
        isTemporary: isTemporarySupplierOption(newOption),
      };
    }
    return null;
  }, [mode, existingId, newOption, existingSuppliers]);

  /**
   * Fornecedor novo no orçamento nunca tem a categoria; para o existente,
   * consultamos a `addedItemTypeList` pelo id interno.
   */
  const targetHasCategory = useMemo(() => {
    if (!target || target.isNew) return false;
    return categoryExistsIn(target.existingSupplierId ?? target.realSupplierId);
  }, [target, categoryExistsIn]);

  /** Só calculado no passo de confirmação — é onde o número é exibido. */
  const projection = useMemo(() => {
    if (step !== "confirm" || !target || !projectValue) return null;
    return projectValue(target);
  }, [step, target, projectValue]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!target) return;
    setSaving(true);
    const ok = await onConfirm(target);
    setSaving(false);
    // Em caso de falha o handler já notificou o usuário; mantém o passo de
    // confirmação aberto para ele poder tentar de novo.
    if (ok) setStep("done");
  };

  // Não é exibido: nomeia a janela para leitores de tela (aria-label).
  const title =
    step === "done"
      ? "Transferência realizada"
      : step === "confirm"
      ? "Confirmar transferência"
      : "Transferir serviço para outro fornecedor";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-label={title}
    >
      <ModalCloseBar onClose={handleClose} />

      <DialogContent dividers sx={{ px: 3, py: 2.5, borderTop: "none" }}>
        {step === "done" && (
          <SuccessStep
            message={
              <>
                Serviço <Highlight>{serviceLabel}</Highlight> transferido com
                sucesso para o fornecedor{" "}
                <Highlight>{target?.supplierName}</Highlight>.
              </>
            }
            detail="Os totais do orçamento foram atualizados. A ação foi registrada na auditoria."
          />
        )}

        {step === "confirm" && (
          <>
            <WarningHero>
              Deseja transferir o serviço <Highlight>{serviceLabel}</Highlight>{" "}
              do fornecedor <Highlight>{currentSupplierLabel}</Highlight> para o
              fornecedor <Highlight>{target?.supplierName}</Highlight>?
            </WarningHero>

            {target?.isNew && (
              <WarningBox>
                O fornecedor selecionado ainda não faz parte desta forma de
                cobrança. Ao confirmar, ele será adicionado automaticamente à
                forma de cobrança <Highlight>{calcLabel}</Highlight>.
              </WarningBox>
            )}

            {targetHasCategory ? (
              /* Taxas, impostos e câmbio pertencem à CATEGORIA, não ao serviço
                 (ver `serviceBaseValue` em `calc-methods.ts`). Entrando numa
                 categoria que já existe, o serviço passa a ser calculado com o
                 perfil fiscal de lá — e o valor pode mudar. */
              <WarningBox>
                A categoria <Highlight>{categoryLabel}</Highlight> já existe no
                fornecedor <Highlight>{target?.supplierName}</Highlight>, e o
                serviço será adicionado a ela. As{" "}
                <Highlight>taxas, impostos e câmbio</Highlight> dessa categoria
                passarão a valer para o serviço — o valor dele pode mudar.
              </WarningBox>
            ) : (
              <InfoBox>
                A categoria <Highlight>{categoryLabel}</Highlight> ainda não
                existe no fornecedor{" "}
                <Highlight>{target?.supplierName}</Highlight>. Ela será criada
                automaticamente com as{" "}
                <Highlight>mesmas taxas, impostos e câmbio</Highlight> da
                categoria de origem, preservando o valor do serviço. Os
                descritivos da categoria (composição, política de cancelamento e
                forma de pagamento) não são copiados.
              </InfoBox>
            )}

            {projection && <ValueProjection projection={projection} />}

            <ConsequenceList items={CONSEQUENCES} />
          </>
        )}

        {(step === "mode" || step === "pick") && (
          <>
            <ModalHero icon={<MdPersonAddAlt1 size={26} />}>
              Transfira este serviço para outro fornecedor da mesma forma de
              cobrança.
            </ModalHero>

            <SummaryBlock>
              <SummaryRow label="Serviço" value={serviceLabel} />
              <SummaryRow label="Categoria atual" value={categoryLabel} />
              <SummaryRow
                label="Fornecedor atual"
                value={currentSupplierLabel}
              />
              <SummaryRow label="Forma de cobrança" value={calcLabel} />
            </SummaryBlock>

            {step === "mode" && (
              <>
                <SectionLabel
                  text="Escolha o destino"
                  hint="O fornecedor de destino precisa estar na mesma forma de cobrança do serviço. Se ele ainda não estiver no orçamento, use 'Novo fornecedor'."
                />

                <ChoiceCard
                  selected={mode === "existing"}
                  disabled={!hasExistingSuppliers}
                  icon={<MdBusiness size={19} />}
                  title="Fornecedor existente"
                  description={
                    <>
                      Transferir para um fornecedor já cadastrado na forma de
                      cobrança atual (<Highlight>{calcLabel}</Highlight>).
                    </>
                  }
                  onSelect={() => setMode("existing")}
                />

                <ChoiceCard
                  selected={mode === "new"}
                  icon={<MdAdd size={20} />}
                  title="Novo fornecedor"
                  description="Adicionar um fornecedor ao orçamento nesta forma de cobrança e transferir o serviço."
                  onSelect={() => setMode("new")}
                />

                {!hasExistingSuppliers && (
                  <Typography
                    sx={{ fontSize: 12.5, color: COLORS.warnIcon, mt: 1 }}
                  >
                    Não existem outros fornecedores cadastrados nesta forma de
                    cobrança. Selecione "Novo fornecedor" para continuar.
                  </Typography>
                )}
              </>
            )}

            {step === "pick" && mode === "existing" && (
              <>
                <SectionLabel
                  text="Fornecedor de destino"
                  hint={`Somente fornecedores já cadastrados na forma de cobrança ${calcOption} são listados.`}
                />
                <Select
                  fullWidth
                  size="small"
                  value={existingId}
                  displayEmpty
                  onChange={(e) => setExistingId(e.target.value)}
                  inputProps={{ "aria-label": "Fornecedor de destino" }}
                  sx={{ borderRadius: "6px", fontSize: 13.5 }}
                >
                  <MenuItem value="" disabled>
                    Selecione um fornecedor
                  </MenuItem>
                  {existingSuppliers.map((supplier) => (
                    <MenuItem
                      key={supplier.supplierId}
                      value={supplier.supplierId}
                      sx={{ fontSize: 13.5 }}
                    >
                      {supplier.label}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {step === "pick" && mode === "new" && (
              <>
                <SectionLabel
                  text="Buscar na base de fornecedores"
                  hint="O fornecedor não precisa estar vinculado ao orçamento — ele será adicionado automaticamente nesta forma de cobrança."
                />
                <SupplierAutoComplete
                  title=""
                  label=""
                  provider_id=""
                  onSelectSupplier={() => undefined}
                  onSelectSupplierOption={(option) => setNewOption(option)}
                  // O fornecedor atual do serviço não pode ser o destino —
                  // exceto o "a definir", que existe justamente para ser
                  // repetido: cada linha reserva um fornecedor real distinto.
                  getOptionDisabled={(option: any) =>
                    option?._id === currentSupplierRealId &&
                    !isTemporarySupplierOption(option)
                  }
                />
                <Typography
                  sx={{ fontSize: 12.5, color: COLORS.muted, mt: 1.25 }}
                >
                  Pesquise por nome, razão social ou CNPJ.
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={modalActionsSx}>
        {step === "mode" && (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={cancelButtonSx}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!mode}
              onClick={() => setStep("pick")}
              sx={primaryButtonSx}
            >
              Avançar
            </Button>
          </>
        )}
        {step === "pick" && (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={cancelButtonSx}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!target}
              onClick={() => setStep("confirm")}
              sx={primaryButtonSx}
            >
              Avançar
            </Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={saving}
              sx={cancelButtonSx}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving}
              sx={primaryButtonSx}
            >
              {saving ? "Transferindo..." : "Transferir serviço"}
            </Button>
          </>
        )}
        {step === "done" && (
          <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferServiceSupplierModal;
